/**
 * O transporte.
 *
 * <h2>Por que não há nenhum token neste arquivo</h2>
 *
 * A sessão do PLANTUS é um cookie `HttpOnly` (ADR-0008): o navegador o
 * envia sozinho, e o JavaScript **não consegue lê-lo**. É essa a proteção
 * — um XSS na página não alcança a credencial.
 *
 * Guardar qualquer cópia do token em `localStorage`, `sessionStorage` ou
 * memória jogaria fora a proteção inteira. Por isso este cliente só
 * precisa de uma coisa: `credentials: "include"`.
 */

export class ErroDaApi extends Error {
  constructor(
    readonly status: number,
    /** Código estável devolvido pela API — `credenciais_invalidas`, etc. */
    readonly codigo: string | null,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = "ErroDaApi";
  }
}

/** A rede não respondeu. Distinto de "a API recusou". */
export class ErroDeRede extends Error {
  constructor(causa: unknown) {
    super("não foi possível falar com o servidor");
    this.name = "ErroDeRede";
    this.cause = causa;
  }
}

interface CorpoDeErro {
  erro?: string;
  mensagem?: string;
}

const BASE = "/api";

export async function requisitar<T>(
  caminho: string,
  opcoes: { metodo?: string; corpo?: unknown } = {},
): Promise<T> {
  const { metodo = "GET", corpo } = opcoes;

  let resposta: Response;
  try {
    resposta = await fetch(BASE + caminho, {
      method: metodo,
      // O cookie de sessão viaja daqui. Sem isto, toda chamada é anônima.
      credentials: "include",
      headers: corpo === undefined ? {} : { "Content-Type": "application/json" },
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    });
  } catch (causa) {
    throw new ErroDeRede(causa);
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const erro = (dados ?? {}) as CorpoDeErro;
    throw new ErroDaApi(
      resposta.status,
      erro.erro ?? null,
      erro.mensagem ?? erro.erro ?? "erro na requisição",
    );
  }

  return dados as T;
}
